import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrashAlt, FaChevronDown, FaSlidersH } from "react-icons/fa";
import "../assets/css/Projects.css";
import "../assets/css/DigixLeads.css";
import axiosInstance from "../components/api/axiosInstance";
import { SupportSortableHeader } from "./SupportSortableHeader";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from "../components/TablePagination";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import useCompactViewport from "../hooks/useCompactViewport";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";
import { useResizableColumns } from "./issues/useResizableColumns";
import { getAuthItem } from "../utils/authStorage";
import { useConfirm } from "../components/ConfirmModal";
import {
  getCustomerSelectLabel,
  mapCustomerListItemFromApi
} from "../utils/formatters";
import TenantSelectBar from "../components/TenantSelectBar";

const PROJECTS_TABLE_STORAGE_KEY = "cloudOffice_projectsTable_v2";
const PROJECTS_ROWS_PER_PAGE_KEY = "projectsRowsPerPage";

function parsePossibleIds(value) {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value
      .map((v) => Number(v))
      .filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((v) => Number(String(v).trim()))
      .filter((n) => Number.isFinite(n) && n > 0);
  }
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? [n] : [];
}

export function readPersistedProjectsTableState() {
  try {
    // Prefer session state (user/session-specific), but fall back to localStorage.
    const raw =
      sessionStorage.getItem(PROJECTS_TABLE_STORAGE_KEY) ??
      localStorage.getItem(PROJECTS_TABLE_STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    const page = Math.max(1, parseInt(s.currentPage, 10) || 1);
    return {
      currentPage: page,
      nameFilter: typeof s.nameFilter === "string" ? s.nameFilter : "",
      statusFilter: typeof s.statusFilter === "string" ? s.statusFilter : "",
      categoryFilter: typeof s.categoryFilter === "string" ? s.categoryFilter : "",
      selectedCustomerId: typeof s.selectedCustomerId === "string" ? s.selectedCustomerId : ""
    };
  } catch {
    return null;
  }
}

function Projects({
  projects = [],
  loading = false,
  onAddProjectClick,
  onViewProject,
  onEditProject,
  onFetchProjects,
  dashboardProjectStatusPreset = ""
}) {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const role = (() => {
    try {
      const u = getAuthItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  })();
  const canAdd = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.ADD, role);
  const canEdit = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.EDIT, role);
  const canDelete = canPerformActionOnPage(PAGES.PROJECTS, ACTIONS.DELETE, role);

  const persistedOnce = useMemo(() => readPersistedProjectsTableState(), []);

  const [currentPage, setCurrentPage] = useState(() => persistedOnce?.currentPage ?? 1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(PROJECTS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_ROWS_PER_PAGE;
  });
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(() => persistedOnce?.selectedCustomerId ?? "");
  const [, setLoadingCustomers] = useState(true);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [loadingStatuses, setLoadingStatuses] = useState(true);

  const [statusFilter, setStatusFilter] = useState(() => persistedOnce?.statusFilter ?? "");
  const [categoryFilter, setCategoryFilter] = useState(() => persistedOnce?.categoryFilter ?? "");
  const [nameFilter, setNameFilter] = useState(() => persistedOnce?.nameFilter ?? "");
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    nameFilter: persistedOnce?.nameFilter ?? "",
    statusFilter: persistedOnce?.statusFilter ?? "",
    categoryFilter: persistedOnce?.categoryFilter ?? "",
    selectedCustomerId: persistedOnce?.selectedCustomerId ?? ""
  }));
  const [freeSearch, setFreeSearch] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [dashboardPresetActive, setDashboardPresetActive] = useState(() => Boolean(dashboardProjectStatusPreset));
  const isCompactViewport = useCompactViewport();
  const [filtersOpen, setFiltersOpen] = useState(false);

  const PROJECTS_COL_DEFAULT_WIDTHS = [160, 120, 160, 140, 140, 160, 120, 130, 160, 130, 130, 160, 120, 120];
  const { getColProps, ResizeHandle } = useResizableColumns(PROJECTS_COL_DEFAULT_WIDTHS);

  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState("");
  const categoryDropdownRef = useRef(null);
  const currentUserId = useMemo(() => {
    try {
      const u = JSON.parse(getAuthItem("user") || "{}");
      const n = Number(u.user_id ?? u.User_Id ?? u.id);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  }, []);

  const isProjectAssignedToCurrentUser = (project) => {
    if (currentUserId == null) return false;
    const ids = [];
    const users = Array.isArray(project?.Users)
      ? project.Users
      : Array.isArray(project?.users)
        ? project.users
        : [];
    for (const u of users) {
      const uid = Number(u?.User_Id ?? u?.user_id ?? u?.id);
      if (Number.isFinite(uid) && uid > 0) ids.push(uid);
    }
    ids.push(
      ...parsePossibleIds(project?.User_Ids),
      ...parsePossibleIds(project?.user_ids),
      ...parsePossibleIds(project?.Assigned_User_Ids),
      ...parsePossibleIds(project?.assigned_user_ids),
      ...parsePossibleIds(project?.Users_Ids),
      ...parsePossibleIds(project?.users_ids)
    );
    return Array.from(new Set(ids)).includes(Number(currentUserId));
  };
  const isActiveProjectRow = (project) => {
    const status = String(
      project?.Status ??
        project?.status ??
        project?.Status_Name ??
        project?.status_name ??
        project?.Project_Status ??
        project?.project_status ??
        ""
    )
      .trim()
      .toLowerCase();
    return status === "active";
  };
  const isNewProjectRow = (project) => {
    const status = String(
      project?.Status ??
        project?.status ??
        project?.Status_Name ??
        project?.status_name ??
        project?.Project_Status ??
        project?.project_status ??
        ""
    )
      .trim()
      .toLowerCase();
    return status.includes("new");
  };
  const isCompletedProjectRow = (project) => {
    const status = String(
      project?.Status ??
        project?.status ??
        project?.Status_Name ??
        project?.status_name ??
        project?.Project_Status ??
        project?.project_status ??
        ""
    )
      .trim()
      .toLowerCase();
    return (
      status.includes("completed") ||
      status.includes("complete") ||
      status.includes("resolved") ||
      status.includes("closed") ||
      status.includes("done")
    );
  };

  const normalizeStatus = (value) => String(value || "").trim().toLowerCase();
  const getProjectStatusValue = (project) =>
    normalizeStatus(
      project?.Status ??
        project?.status ??
        project?.Status_Name ??
        project?.status_name ??
        project?.Project_Status ??
        project?.project_status ??
        ""
    );
  const getProjectCategoryId = (project) =>
    Number(project?.Category_Id ?? project?.category_id ?? project?.categoryId ?? project?.CategoryID);
  const getProjectCustomerId = (project) =>
    Number(project?.client_id ?? project?.client_id ?? project?.customerId ?? project?.CustomerID);

  const freeSearchFilteredProjects = useMemo(() => {
    if (!String(appliedFilters.selectedCustomerId || "").trim()) return [];
    const q = String(freeSearch || "").trim().toLowerCase();
    let rows = Array.isArray(projects) ? projects : [];
    const hasAppliedFilters =
      String(appliedFilters.statusFilter || "").trim() !== "" ||
      String(appliedFilters.categoryFilter || "").trim() !== "" ||
      String(appliedFilters.nameFilter || "").trim() !== "";

    // Dashboard preset applies only for the initial preset view.
    // Once user applies filters manually, those filters take precedence.
    if (dashboardPresetActive && !hasAppliedFilters) {
      if (dashboardProjectStatusPreset === "active") {
        rows = rows.filter(isActiveProjectRow);
      } else if (dashboardProjectStatusPreset === "new") {
        rows = rows.filter(isNewProjectRow);
      } else if (dashboardProjectStatusPreset === "completed") {
        rows = rows.filter(isCompletedProjectRow);
      } else if (dashboardProjectStatusPreset === "assigned") {
        rows = rows.filter(isProjectAssignedToCurrentUser);
      }
    }
    const statusNeedle = normalizeStatus(appliedFilters.statusFilter);
    if (statusNeedle) {
      rows = rows.filter((p) => getProjectStatusValue(p) === statusNeedle);
    }

    const categoryNeedle = Number(appliedFilters.categoryFilter);
    if (Number.isFinite(categoryNeedle) && categoryNeedle > 0) {
      rows = rows.filter((p) => getProjectCategoryId(p) === categoryNeedle);
    }

    const customerNeedle = Number(appliedFilters.selectedCustomerId);
    if (Number.isFinite(customerNeedle) && customerNeedle > 0) {
      const selectedCustomer = customers.find((c) => Number(c.id) === customerNeedle);
      const selectedCustomerName = selectedCustomer
        ? String(getCustomerSelectLabel(selectedCustomer)).trim().toLowerCase()
        : "";
      rows = rows.filter((p) => {
        const projectCustomerId = getProjectCustomerId(p);
        if (Number.isFinite(projectCustomerId) && projectCustomerId > 0) {
          return projectCustomerId === customerNeedle;
        }
        const projectCustomerName = String(p?.Customer_Name ?? p?.customer_name ?? "")
          .trim()
          .toLowerCase();
        return (
          !!selectedCustomerName &&
          (projectCustomerName === selectedCustomerName ||
            projectCustomerName.includes(selectedCustomerName))
        );
      });
    }

    const nameNeedle = String(appliedFilters.nameFilter || "").trim().toLowerCase();
    if (nameNeedle) {
      rows = rows.filter((p) =>
        String(p?.Project_Name ?? p?.project_name ?? "").toLowerCase().includes(nameNeedle)
      );
    }

    if (!q) return rows;
    return rows.filter((p) => {
      const bag = [
        p?.Project_Name ?? p?.project_name ?? "",
        p?.Short_Code ?? p?.short_code ?? "",
        p?.Customer_Name ?? p?.customer_name ?? "",
        p?.Domain ?? p?.domain ?? "",
        p?.Category ?? p?.category ?? "",
        p?.Priority_Seq ?? p?.priority_seq ?? "",
        p?.Status ?? p?.status ?? "",
        p?.Visibility ?? p?.visibility ?? "",
        p?.Total_Cost ?? p?.total_cost ?? ""
      ]
        .map((v) => String(v || "").toLowerCase());
      return bag.some((v) => v.includes(q));
    });
  }, [
    projects,
    customers,
    freeSearch,
    dashboardProjectStatusPreset,
    appliedFilters
  ]);

  const sortedProjects = useMemo(() => {
    if (!sortColumn) return freeSearchFilteredProjects;
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...freeSearchFilteredProjects].sort((a, b) => {
      const va = getProjectSortValue(a, sortColumn);
      const vb = getProjectSortValue(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a.Project_Id || a.project_id || a.id || 0);
      const idb = Number(b.Project_Id || b.project_id || b.id || 0);
      return (ida - idb) * mult;
    });
  }, [freeSearchFilteredProjects, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedProjects.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentProjects = sortedProjects.slice(startIndex, endIndex);

  useEffect(() => {
    if (loading) return;
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage, loading]);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setCurrentPage(1);
    localStorage.setItem(PROJECTS_ROWS_PER_PAGE_KEY, String(next));
  };

  useEffect(() => {
    try {
      const payload = JSON.stringify({
        currentPage,
        nameFilter,
        statusFilter,
        categoryFilter,
        selectedCustomerId
      });
      sessionStorage.setItem(PROJECTS_TABLE_STORAGE_KEY, payload);
      localStorage.setItem(PROJECTS_TABLE_STORAGE_KEY, payload);
    } catch {
      /* ignore quota/private mode */
    }
  }, [currentPage, nameFilter, statusFilter, categoryFilter, selectedCustomerId]);

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoadingCustomers(true);
      try {
        const response = await axiosInstance.get("/clients");
        const raw = response.data;
        const arr = Array.isArray(raw) ? raw : raw?.data || [];
        const mapped = (arr || []).map(mapCustomerListItemFromApi).filter(Boolean);
        setCustomers(mapped);
      } catch (e) {
        console.error("Error fetching customers for projects filter:", e);
        setCustomers([]);
      } finally {
        setLoadingCustomers(false);
      }
    };
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const response = await axiosInstance.get("/project-categories");
        const raw = response.data;
        const arr = Array.isArray(raw) ? raw : raw?.data || [];
        const mapped = (arr || []).map((c) => {
          const id = c.Category_Id || c.category_id || c.id;
          const name = c.Category_Name || c.category_name || c.name || "—";
          return { id, name };
        }).filter((x) => x.id);
        setCategories(mapped);
      } catch (e) {
        console.error("Error fetching project categories for projects filter:", e);
        setCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    const fetchStatuses = async () => {
      setLoadingStatuses(true);
      try {
        const response = await axiosInstance.get("/project-statuses");
        const raw = response.data;
        const arr = Array.isArray(raw) ? raw : raw?.data || [];
        const mapped = (arr || [])
          .map((s) => ({
            id: s.Status_Id ?? s.status_id ?? s.id,
            name: s.Status ?? s.status ?? s.name ?? "—"
          }))
          .filter((x) => x.id && String(x.name || "").trim() !== "")
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setStatuses(mapped);
      } catch (e) {
        console.error("Error fetching project statuses for projects filter:", e);
        setStatuses([]);
      } finally {
        setLoadingStatuses(false);
      }
    };
    fetchCustomers();
    fetchCategories();
    fetchStatuses();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target)) { setCategoryDropdownOpen(false); setCategorySearchQuery(""); }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const applyFilters = ({ customerId, status, category, name } = {}) => {
    if (!onFetchProjects) return;
    const effectiveCustomerId = customerId !== undefined ? customerId : selectedCustomerId;
    if (!effectiveCustomerId) return;
    const effectiveStatus = status !== undefined ? status : statusFilter;
    const effectiveCategory = category !== undefined ? category : categoryFilter;
    const effectiveName = name !== undefined ? name : nameFilter;
    const hasNonCustomerFilters =
      !!String(effectiveStatus || "").trim() ||
      !!String(effectiveCategory || "").trim() ||
      !!String(effectiveName || "").trim();

    const filters = {
      // Backend /search-projects currently errors with client_id in some combinations.
      // Use customerId only for pure customer filtering; combine via client-side otherwise.
      customerId: !hasNonCustomerFilters && effectiveCustomerId ? Number(effectiveCustomerId) : undefined,
      customerName: effectiveCustomerId
        ? (() => {
            const selected = customers.find((c) => String(c.id) === String(effectiveCustomerId));
            return selected ? getCustomerSelectLabel(selected) : undefined;
          })()
        : undefined,
      status: effectiveStatus || undefined,
      // keep category as numeric id so API can use category_id
      categoryId: effectiveCategory ? Number(effectiveCategory) : undefined,
      name: (effectiveName || "").trim() || undefined
    };

    // If no filters at all, fetch all projects
    if (!filters.customerId && !filters.customerName && !filters.status && !filters.categoryId && !filters.name) {
      onFetchProjects({});
    } else {
      onFetchProjects(filters);
    }
  };

  useEffect(() => {
    if (!dashboardProjectStatusPreset) return;
    setDashboardPresetActive(true);
    setCurrentPage(1);
    // Preset filtering happens client-side within the selected tenant's projects.
    setCategoryFilter("");
    setNameFilter("");
    setFreeSearch("");
    const presetStatus =
      dashboardProjectStatusPreset === "new"
        ? "New"
        : dashboardProjectStatusPreset === "active"
          ? "Active"
          : dashboardProjectStatusPreset === "completed"
            ? "Completed"
            : "";
    setStatusFilter(presetStatus);
    setAppliedFilters({
      nameFilter: "",
      statusFilter: presetStatus,
      categoryFilter: "",
      selectedCustomerId
    });

    // Presets are handled in freeSearchFilteredProjects; fetch the tenant's unfiltered rows.
    applyFilters({ status: "", category: "", name: "" });
  }, [dashboardProjectStatusPreset]);

  useEffect(() => {
    if (dashboardPresetActive) return;
    setAppliedFilters({
      nameFilter,
      statusFilter,
      categoryFilter,
      selectedCustomerId
    });
    applyFilters({
      customerId: selectedCustomerId,
      status: statusFilter,
      category: categoryFilter,
      name: nameFilter
    });
  }, [nameFilter, statusFilter, categoryFilter, selectedCustomerId, dashboardPresetActive]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "—";
    }
  };

  function getProjectSortValue(project, columnId) {
    const rawDate = (v) => {
      if (!v) return 0;
      const t = new Date(v).getTime();
      return Number.isFinite(t) ? t : 0;
    };
    switch (columnId) {
      case "projectName":
        return String(project.Project_Name || project.project_name || "").toLowerCase();
      case "shortCode":
        return String(project.Short_Code || project.short_code || "").toLowerCase();
      case "customer":
        return String(project.Customer_Name || project.customer_name || "").toLowerCase();
      case "domain":
        return String(project.Domain || project.domain || "").toLowerCase();
      case "category":
        return String(project.Category || project.category || "").toLowerCase();
      case "prioritySeq":
        return Number(project.Priority_Seq || project.priority_seq || 0);
      case "status":
        return String(project.Status || project.status || "").toLowerCase();
      case "startDate":
        return rawDate(project.Start_Date || project.start_date);
      case "actualStartDate":
        return rawDate(project.Actual_Start_Date || project.actual_start_date || project.Actual_StartDate || project.actual_startDate);
      case "endDate":
        return rawDate(project.End_Date || project.end_date);
      case "actualEndDate":
        return rawDate(project.Actual_End_Date || project.Actual_Date || project.actual_end_date || project.actual_date);
      case "totalCost":
        return Number(String(project.Total_Cost ?? project.total_cost ?? 0).replace(/,/g, "")) || 0;
      case "visibility":
        return String(project.Visibility || project.visibility || "").toLowerCase();
      default:
        return "";
    }
  }

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const formatProjectCurrencyAmount = (proj) => {
    const raw = proj?.Total_Cost ?? proj?.total_cost ?? proj?.TotalCost ?? proj?.totalCost;
    if (raw == null || raw === "") return "—";
    const n = Number(String(raw).replace(/,/g, ""));
    if (Number.isNaN(n)) return String(raw);

    const sym = proj?.Currency_Symbol ?? proj?.currency_symbol ?? "";
    const code = String(proj?.Currency_Code ?? proj?.currency_code ?? "").trim().toUpperCase();
    const symbolFromCode = code === "INR" ? "₹" : code === "USD" ? "$" : "";

    const formatted = n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (sym) return `${sym}${formatted}`;
    if (symbolFromCode) return `${symbolFromCode}${formatted}`;
    if (code) return `${code} ${formatted}`;
    return formatted;
  };

  const refreshProjects = () => {
    applyFilters();
  };

  const handleDeactivateProject = async (project) => {
    const id = project.Project_Id || project.project_id || project.id;
    if (!id) return;
    const currentStatus = (project.Status || project.status || "").toLowerCase();
    if (currentStatus === "inactive" || currentStatus === "deactivated") {
      alert("This project is already inactive.");
      return;
    }
    if (!(await confirm({ title: "Deactivate Project", message: "Are you sure you want to deactivate this project?" }))) {
      return;
    }
    // Determine user performing the action
    let updatedBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.id || 1;
      }
    } catch (e) {
      console.error("Error reading user for deactivate:", e);
    }
    try {
      const payload = {
        Status: "Inactive",
        Updated_By: updatedBy
      };
      const response = await axiosInstance.put(`/project/${id}`, payload);
      const message = response.data?.message || "Project deactivated successfully.";
      alert(message);
      refreshProjects();
    } catch (err) {
      console.error("Error deactivating project:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to deactivate project.");
    }
  };

  return (
    <div className="projects-container">
      <div className="main-card">
        <div className="page-header">
          <div className={`projects-header-row co-filters-collapsible${filtersOpen || !isCompactViewport ? " is-open" : ""}`}>
            <button
              type="button"
              className="co-filters-toggle"
              onClick={() => setFiltersOpen((o) => !o)}
              aria-expanded={filtersOpen}
              aria-label={filtersOpen ? "Hide Filters" : "Show Filters"}
              title={filtersOpen ? "Hide Filters" : "Show Filters"}
            >
              <FaSlidersH aria-hidden />
              <span className="co-filters-toggle__label">{filtersOpen ? "Hide Filters" : "Show Filters"}</span>
            </button>
            <div className="co-filters-body">
            <div className="projects-filters-row">
              <TenantSelectBar
                inline
                value={selectedCustomerId}
                onChange={(value) => {
                  setDashboardPresetActive(false);
                  setSelectedCustomerId(value);
                  setCurrentPage(1);
                }}
                hint="Select a tenant to load its projects."
              />
              <input
                type="text"
                placeholder="Search by project name..."
                value={nameFilter}
                onChange={(e) => {
                  const value = e.target.value;
                  setDashboardPresetActive(false);
                  setNameFilter(value);
                  setCurrentPage(1);
                }}
                style={{
                  height: 38,
                  borderRadius: 5,
                  border: "1px solid #D6E0EA",
                  padding: "0 10px",
                  fontSize: 14,
                  minWidth: 180
                }}
              />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setDashboardPresetActive(false);
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  height: 38,
                  borderRadius: 5,
                  border: "1px solid #D6E0EA",
                  padding: "0 12px",
                  background: "#fff",
                  fontSize: 14,
                  minWidth: 140
                }}
              >
                <option value="">All Status</option>
                {loadingStatuses ? (
                  <option value="" disabled>
                    Loading statuses...
                  </option>
                ) : (
                  statuses.map((s) => (
                    <option key={s.id} value={s.name}>
                      {s.name}
                    </option>
                  ))
                )}
              </select>
              <div className="domain-filter-dropdown" ref={categoryDropdownRef} style={{ minWidth: 160 }}>
                <div className="search-filter-input search-filter-select domain-filter-trigger" style={{ height: 38, borderRadius: 5, border: "1px solid #D6E0EA", padding: "0 12px", fontSize: 14, background: "#fff", minWidth: 160, opacity: loadingCategories ? 0.6 : 1, cursor: loadingCategories ? "default" : "pointer" }} onClick={() => !loadingCategories && setCategoryDropdownOpen((o) => !o)} role="button" tabIndex={loadingCategories ? -1 : 0} onKeyDown={(e) => { if (!loadingCategories && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setCategoryDropdownOpen((o) => !o); } }}>
                  <span>{loadingCategories ? "Loading categories..." : (categoryFilter ? (categories.find((c) => String(c.id) === String(categoryFilter))?.name || "All Categories") : "All Categories")}</span>
                  <FaChevronDown className={`domain-filter-chevron ${categoryDropdownOpen ? "open" : ""}`} />
                </div>
                {categoryDropdownOpen && !loadingCategories && (
                  <div className="domain-filter-panel">
                    <input type="text" className="domain-filter-search" placeholder="Search categories..." value={categorySearchQuery} onChange={(e) => setCategorySearchQuery(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    <div className="domain-filter-options">
                      <div className={`domain-filter-option ${!categoryFilter ? "selected" : ""}`} onClick={() => { setDashboardPresetActive(false); setCategoryFilter(""); setCurrentPage(1); setCategoryDropdownOpen(false); setCategorySearchQuery(""); }}>All Categories</div>
                      {categories.filter((c) => !categorySearchQuery.trim() || String(c.name || "").toLowerCase().includes(categorySearchQuery.trim().toLowerCase())).map((c) => (
                        <div key={c.id} className={`domain-filter-option ${String(categoryFilter) === String(c.id) ? "selected" : ""}`} onClick={() => { setDashboardPresetActive(false); setCategoryFilter(String(c.id)); setCurrentPage(1); setCategoryDropdownOpen(false); setCategorySearchQuery(""); }}>{c.name}</div>
                      ))}
                      {categories.filter((c) => !categorySearchQuery.trim() || String(c.name || "").toLowerCase().includes(categorySearchQuery.trim().toLowerCase())).length === 0 && <div className="domain-filter-no-results">No categories found</div>}
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="btn-clear"
                onClick={() => {
                  setDashboardPresetActive(false);
                  setNameFilter("");
                  setStatusFilter("");
                  setCategoryFilter("");
                  setAppliedFilters({
                    nameFilter: "",
                    statusFilter: "",
                    categoryFilter: "",
                    selectedCustomerId
                  });
                  setCurrentPage(1);
                  setCategoryDropdownOpen(false);
                  setCategorySearchQuery("");
                  applyFilters({ status: "", category: "", name: "" });
                }}
              >
                Clear
              </button>
              {canAdd && (
                <div className="header-actions" style={{ marginLeft: 0 }}>
                  <button
                    className="btn-add-lead"
                    onClick={onAddProjectClick || (() => navigate("/projects/add"))}
                  >
                    New Project
                  </button>
                </div>
              )}
            </div>
            </div>
          </div>
        </div>

        <div className="search-row-2 co-results-sticky">
          <div className="free-search-bar" style={{ marginLeft: "auto", maxWidth: 280 }}>
            <input
              type="text"
              className="free-search-input"
              placeholder="Search..."
              value={freeSearch}
              onChange={(e) => {
                setFreeSearch(e.target.value);
                setCurrentPage(1);
              }}
              title="Search across project table columns"
            />
          </div>
        </div>

        <div className="table-wrapper co-desktop-table">
          <div className="table-container">
            <table className="data-table projects-table-frozen">
              <thead>
                <tr>
                  <th className="col-sticky col-0 projects-th-sortable" {...getColProps(0)}>
                    <div className="projects-th-sort-row">
                      <SupportSortableHeader columnId="projectName" label="Project Name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                    </div>
                    <ResizeHandle index={0} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(1)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="shortCode" label="Short Code" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={1} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(2)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="customer" label="Tenant" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={2} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(3)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="domain" label="Domain" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={3} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(4)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="category" label="Category" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={4} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(5)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="prioritySeq" label="Priority Sequence" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={5} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(6)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={6} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(7)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="startDate" label="Start Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={7} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(8)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="actualStartDate" label="Actual Start Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={8} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(9)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="endDate" label="End Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={9} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(10)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="actualEndDate" label="Actual End Date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={10} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(11)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="totalCost" label="Total Cost" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={11} />
                  </th>
                  <th className="projects-th-sortable" {...getColProps(12)}>
                    <div className="projects-th-sort-row projects-th-sort-row--center"><SupportSortableHeader columnId="visibility" label="Visibility" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} /></div>
                    <ResizeHandle index={12} />
                  </th>
                  <th className="col-sticky col-action" {...getColProps(13)}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>Actions</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="14" style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                      Loading projects...
                    </td>
                  </tr>
                ) : projects.length === 0 ? (
                  <tr>
                    <td colSpan="14" style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                      {selectedCustomerId
                        ? 'No projects found. Click "New Project" to add a project.'
                        : "Select a tenant above to view its projects."}
                    </td>
                  </tr>
                ) : (
                  currentProjects.map((project) => (
                    <tr key={project.Project_Id || project.project_id || project.id}>
                      <td className="col-sticky col-0">
                        {onViewProject ? (
                          <span
                            className="table-name-link"
                            onClick={() => onViewProject(project.Project_Id || project.project_id || project.id)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onViewProject(project.Project_Id || project.project_id || project.id);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            style={{ cursor: "pointer", color: "#2977ed" }}
                          >
                            {project.Project_Name || project.project_name || "—"}
                          </span>
                        ) : (
                          project.Project_Name || project.project_name || "—"
                        )}
                      </td>
                      <td>{project.Short_Code || project.short_code || "—"}</td>
                      <td>{project.Customer_Name || project.customer_name || "—"}</td>
                      <td>{project.Domain || project.domain || "—"}</td>
                      <td>{project.Category || project.category || "—"}</td>
                      <td>{project.Priority_Seq || project.priority_seq || "—"}</td>
                      <td>
                        <span className={`status-badge status-${(project.Status || project.status || "").toLowerCase().replace(/\s/g, "-")}`}>
                          {project.Status || project.status || "—"}
                        </span>
                      </td>
                      <td>{formatDate(project.Start_Date || project.start_date)}</td>
                      <td>{formatDate(project.Actual_Start_Date || project.actual_start_date || project.Actual_StartDate || project.actual_startDate)}</td>
                      <td>{formatDate(project.End_Date || project.end_date)}</td>
                      <td>{formatDate(project.Actual_End_Date || project.Actual_Date || project.actual_end_date || project.actual_date)}</td>
                      <td>{formatProjectCurrencyAmount(project)}</td>
                      <td>{project.Visibility || project.visibility || "—"}</td>
                      <td className="col-sticky col-action">
                        <div className="project-actions-cell">
                          {canEdit && (
                            <button
                              type="button"
                              className="project-action-btn project-action-btn-icon project-action-btn-edit"
                              title="Edit project"
                              onClick={() => {
                                const id = project.Project_Id || project.project_id || project.id;
                                if (onEditProject && id) {
                                  onEditProject(id);
                                } else if (id) {
                                  navigate(`/projects/${id}/edit`);
                                }
                              }}
                            >
                              <FaEdit />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              className="project-action-btn project-action-btn-icon project-action-btn-deactivate"
                              title="Deactivate project"
                              onClick={() => handleDeactivateProject(project)}
                            >
                              <FaTrashAlt />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ResultsMobileList
          loading={loading}
          loadingLabel="Loading projects…"
          emptyLabel={
            selectedCustomerId
              ? 'No projects found. Click "New Project" to add a project.'
              : "Select a tenant above to view its projects."
          }
        >
          {!loading &&
            currentProjects.map((project) => {
              const id = project.Project_Id || project.project_id || project.id;
              const name = project.Project_Name || project.project_name || "—";
              const status = project.Status || project.status || null;
              return (
                <ResultsMobileCard
                  key={id || name}
                  title={name}
                  subtitle={project.Customer_Name || project.customer_name || null}
                  meta={[status, project.Category || project.category].filter(Boolean)}
                  fields={[
                    { label: "Code", value: project.Short_Code || project.short_code },
                    { label: "Domain", value: project.Domain || project.domain },
                    { label: "Start", value: formatDate(project.Start_Date || project.start_date) },
                    { label: "End", value: formatDate(project.End_Date || project.end_date) },
                  ]}
                  onOpen={onViewProject ? () => onViewProject(id) : undefined}
                  actions={
                    <>
                      {canEdit ? (
                        <button
                          type="button"
                          className="project-action-btn project-action-btn-icon project-action-btn-edit"
                          title="Edit project"
                          onClick={() => {
                            if (onEditProject && id) onEditProject(id);
                            else if (id) navigate(`/projects/${id}/edit`);
                          }}
                        >
                          <FaEdit />
                        </button>
                      ) : null}
                      {canDelete ? (
                        <button
                          type="button"
                          className="project-action-btn project-action-btn-icon project-action-btn-deactivate"
                          title="Deactivate project"
                          onClick={() => handleDeactivateProject(project)}
                        >
                          <FaTrashAlt />
                        </button>
                      ) : null}
                    </>
                  }
                />
              );
            })}
        </ResultsMobileList>

        {!loading && sortedProjects.length > 0 ? (
          <TablePagination
            currentPage={currentPage}
            totalItems={sortedProjects.length}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setCurrentPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            itemLabel="projects"
            rowsPerPageSelectId="projects-rows-per-page"
          />
        ) : null}
      </div>
    </div>
  );
}

export default Projects;
